/**
 * Canonical Clarity custom event names.
 *
 * Clarity events carry no properties — the event name is the entire payload.
 * Any dimension you want to slice by must be a custom tag instead.
 *
 * These names are defined once here because custom events created through the
 * API cannot be deleted from the Clarity portal; they can only be removed by
 * deleting the code that emits them. A typo would permanently pollute the
 * project, so call sites must reference this map rather than raw strings.
 */
export const ClarityEvents = {
  // Landing page — call to action buttons.
  // Every CTA navigates to the same destination, so placement is the only
  // thing that distinguishes them. Repeated `set` calls on one tag key
  // accumulate into an unordered array, which cannot attribute a click to the
  // section that earned it, and only events feed funnels and the dashboard.
  // Placement therefore has to be encoded in the event name itself. Each
  // placement event fires alongside the aggregate below.
  landingCtaHero: "landingCtaHero",
  landingCtaBuild: "landingCtaBuild",
  landingCtaReach: "landingCtaReach",
  landingCtaOperate: "landingCtaOperate",
  landingCtaJoin: "landingCtaJoin",
  landingCtaFinal: "landingCtaFinal",
  landingCtaClicked: "landingCtaClicked",

  // Landing page — secondary engagement.
  landingGithubClicked: "landingGithubClicked",
  landingDiscordClicked: "landingDiscordClicked",
  landingFaqExpanded: "landingFaqExpanded",

  // Signup / onboarding form.
  onboardFormViewed: "onboardFormViewed",
  onboardFormBlockedCountry: "onboardFormBlockedCountry",
  onboardFormValidationFailed: "onboardFormValidationFailed",
  onboardStudioNameConflict: "onboardStudioNameConflict",
  onboardAddressSuggestionShown: "onboardAddressSuggestionShown",
  onboardAddressSuggestionAccepted: "onboardAddressSuggestionAccepted",
  onboardAddressSuggestionRejected: "onboardAddressSuggestionRejected",
  onboardFormSubmitted: "onboardFormSubmitted",
  onboardSubmitFailed: "onboardSubmitFailed",

  // Non disclosure agreement.
  ndaSent: "ndaSent",
  ndaResendRequested: "ndaResendRequested",
  ndaSigned: "ndaSigned",

  // Partner Center enrollment.
  pcEnrollmentStarted: "pcEnrollmentStarted",
  pcEnrollmentComplete: "pcEnrollmentComplete",
  pcEnrollmentFailed: "pcEnrollmentFailed",

  // Game concepts.
  gameConceptCreated: "gameConceptCreated",
  gameConceptSubmitted: "gameConceptSubmitted",
  gameConceptResubmitted: "gameConceptResubmitted",
  gameConceptSoftRejectedViewed: "gameConceptSoftRejectedViewed",
  gameConceptDeleted: "gameConceptDeleted",

  // Dashboard.
  dashboardViewed: "dashboardViewed",
  welcomeModalShown: "welcomeModalShown",
  welcomeModalDismissed: "welcomeModalDismissed",
} as const;

export type ClarityEventName = (typeof ClarityEvents)[keyof typeof ClarityEvents];

/**
 * Reasons a session is worth prioritizing past Clarity's daily sampling cap.
 * Kept deliberately short — upgrading every session defeats the purpose.
 */
export const ClarityUpgradeReasons = {
  blockedCountry: "blockedCountry",
  submitFailed: "submitFailed",
  enrollmentFailed: "enrollmentFailed",
  gameConceptSoftRejected: "gameConceptSoftRejected",
} as const;

export type ClarityUpgradeReason =
  (typeof ClarityUpgradeReasons)[keyof typeof ClarityUpgradeReasons];

/**
 * Canonical custom tag keys.
 *
 * Clarity places no limit on the number of custom tags. Setting the same key
 * more than once on a page appends rather than replaces, so a tag whose value
 * changes mid-session surfaces as an array of every value it held.
 */
export const ClarityTags = {
  applicationStatus: "applicationStatus",
  accountType: "accountType",
  pcEnrollmentStatus: "pcEnrollmentStatus",
  countryOrRegion: "countryOrRegion",
  isBlockedCountry: "isBlockedCountry",
  isEmbargoedCountry: "isEmbargoedCountry",
  isFastTrackAccount: "isFastTrackAccount",
  hasSubmittedGameConcept: "hasSubmittedGameConcept",
  hasApprovedGameConcept: "hasApprovedGameConcept",
  sellerId: "sellerId",
  locale: "locale",
} as const;

export type ClarityTagKey = (typeof ClarityTags)[keyof typeof ClarityTags];
